import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase.js'
import { apiFetch } from '../lib/api.js'
import { VEHICLE_TYPES } from '../lib/constants.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { saveSession } from '../lib/session.js'
import { pageBg as podiumBg } from '../lib/images'
import VehiclePicker from '../components/ui/VehiclePicker.jsx'

const REDIRECT_URL = `${window.location.origin}/auth/callback`

function isInAppBrowser() {
  const ua = navigator.userAgent || ''
  return /FBAN|FBAV|Instagram|Twitter|LinkedInApp|Line\/|KAKAOTALK|MicroMessenger|wv\)|WebView/i.test(ua)
}

function InAppBrowserWarning() {
  const [copied, setCopied] = useState(false)
  const url = window.location.href

  function copyUrl() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm border border-orange-400/40 rounded-2xl p-6 text-center space-y-4">
      <div className="text-4xl">⚠️</div>
      <h2 className="text-white font-bold text-lg">Ouvre ce lien dans ton navigateur</h2>
      <p className="text-white/70 text-sm leading-relaxed">
        La connexion Google et Microsoft n'est pas disponible dans le navigateur intégré de Facebook ou d'Instagram.
      </p>
      <p className="text-white/60 text-sm">
        Appuie sur les <strong className="text-white">trois points</strong> en haut à droite, puis <strong className="text-white">"Ouvrir dans Chrome"</strong> (ou Safari).
      </p>
      <button
        onClick={copyUrl}
        className="w-full bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-xl py-3 text-sm transition"
      >
        {copied ? '✓ Lien copié !' : 'Copier le lien'}
      </button>
      <p className="text-white/40 text-xs">Puis colle-le dans ton navigateur</p>
    </div>
  )
}

function SsoButtons({ loading, onSso }) {
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => onSso('google')}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm font-medium text-white hover:bg-white/15 transition disabled:opacity-60"
      >
        <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        Continuer avec Google
      </button>

      <button
        type="button"
        onClick={() => onSso('azure')}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm font-medium text-white hover:bg-white/15 transition disabled:opacity-60"
      >
        <svg width="18" height="18" viewBox="0 0 21 21"><path fill="#f35325" d="M0 0h10v10H0z"/><path fill="#81bc06" d="M11 0h10v10H11z"/><path fill="#05a6f0" d="M0 11h10v10H0z"/><path fill="#ffba08" d="M11 11h10v10H11z"/></svg>
        Continuer avec Microsoft
      </button>
    </div>
  )
}

// Carte invité quand un quiz en attente de partage existe — plein écran style JoinPage
function GuestShareCard({ pendingShare, onBack }) {
  const navigate = useNavigate()
  const [pseudo, setPseudo] = useState('')
  const savedVehicle = localStorage.getItem('solo_vehicle_type')
  const [vehicle, setVehicle] = useState(
    VEHICLE_TYPES.find(v => v.emoji === savedVehicle)?.emoji ?? VEHICLE_TYPES[0].emoji
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handlePlay(e) {
    e.preventDefault()
    if (!pseudo.trim()) return setError('Entre un pseudo.')
    setLoading(true)
    setError('')
    try {
      const data = await apiFetch('/api/games/solo-guest', {
        method: 'POST',
        body: JSON.stringify({ deck_id: pendingShare.deckId, pseudo: pseudo.trim(), type_vehicule: vehicle }),
      })
      localStorage.setItem('solo_vehicle_type', vehicle)
      localStorage.removeItem('kart_pending_share')
      saveSession(data.code, data.session_token, data.kart.id)
      navigate(`/solo/${data.code}`)
    } catch (e) {
      setError(e.message)
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handlePlay} className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="text-center shrink-0">
        <div className="text-3xl mb-1">🎯</div>
        <h2 className="text-white font-semibold text-base">Quiz partagé avec toi</h2>
        <p className="text-white font-medium text-sm mt-0.5">"{pendingShare.deckTitle}"</p>
      </div>

      <input
        value={pseudo}
        onChange={e => setPseudo(e.target.value.slice(0, 20))}
        placeholder="Ton pseudo…"
        autoFocus
        className="shrink-0 w-full text-center text-xl font-semibold border-2 border-white/30 bg-white/10 text-white placeholder-white/40 rounded-2xl px-4 py-3 focus:outline-none focus:border-white"
        maxLength={20}
      />

      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <p className="text-white font-medium text-sm">Ton véhicule</p>
          <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2.5 py-1">
            <TwemojiImg emoji={vehicle} size={16} />
            <span className="text-white text-xs font-medium">
              {VEHICLE_TYPES.find(v => v.emoji === vehicle)?.label}
            </span>
          </div>
        </div>
        <VehiclePicker value={vehicle} onChange={setVehicle} />
      </div>

      {error && (
        <p className="shrink-0 text-red-300 text-sm text-center bg-red-500/20 rounded-xl py-2 px-3">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading || !pseudo.trim()}
        className="shrink-0 w-full bg-white text-primary font-semibold py-3 rounded-2xl text-lg disabled:opacity-50 active:scale-95 transition"
      >
        {loading ? '…' : '🚀 Jouer !'}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="shrink-0 w-full text-white/60 hover:text-white text-sm transition text-center"
      >
        ← Retour à la connexion
      </button>
    </form>
  )
}

function GuestCard({ onBack }) {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    const clean = code.trim().toUpperCase()
    if (clean.length !== 6) return setError('Le code fait 6 caractères.')
    setLoading(true)
    setError('')
    try {
      await apiFetch(`/api/games/${clean}`)
      navigate(`/join/${clean}`)
    } catch {
      setError('Partie introuvable. Vérifie le code.')
    }
    setLoading(false)
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-4xl mb-2">🏁</div>
        <h2 className="text-white font-semibold text-base">Rejoindre une partie</h2>
        <p className="text-white/60 text-sm mt-1">Entre le code affiché par ton prof</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase().slice(0, 6))}
          placeholder="ABC123"
          className="w-full text-center text-3xl font-mono font-semibold tracking-[0.3em] border-2 border-white/30 bg-white/10 text-white placeholder-white/40 rounded-2xl px-4 py-4 focus:outline-none focus:border-white uppercase"
          maxLength={6}
          autoComplete="off"
          autoCapitalize="characters"
          autoFocus
        />
        {error && <p className="text-red-300 text-sm text-center">{error}</p>}
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="w-full bg-white text-primary font-semibold py-3 rounded-2xl text-lg disabled:opacity-50 active:scale-95 transition"
        >
          {loading ? '…' : 'Entrer →'}
        </button>
      </form>
      <button
        type="button"
        onClick={onBack}
        className="w-full text-white/60 hover:text-white text-sm transition text-center"
      >
        ← Retour à la connexion
      </button>
    </div>
  )
}

export default function LoginPage() {
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [guestMode, setGuestMode] = useState(false)
  const [pendingShare, setPendingShare] = useState(null)
  const [shareGuestMode, setShareGuestMode] = useState(false)
  const inApp = isInAppBrowser()

  useEffect(() => {
    const raw = localStorage.getItem('kart_pending_share')
    if (raw) {
      try { setPendingShare(JSON.parse(raw)) } catch { localStorage.removeItem('kart_pending_share') }
    }
  }, [])

  async function handleSso(provider) {
    setError('')
    setLoading(true)
    const options = { redirectTo: REDIRECT_URL }
    if (provider === 'azure') {
      options.scopes = 'email openid profile'
    }
    const { error } = await supabase.auth.signInWithOAuth({ provider, options })
    if (error) setError(error.message)
    setLoading(false)
  }

  const bgStyle = { backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }

  // Vue plein écran pour la saisie pseudo + véhicule (style JoinPage)
  if (shareGuestMode && pendingShare && !inApp) {
    return (
      <div className="h-[100dvh] flex flex-col px-4 pt-4 pb-3" style={bgStyle}>
        <div className="flex-1 min-h-0 w-full max-w-sm mx-auto flex flex-col">
          <div className="flex-shrink-0 flex flex-col items-center pb-2">
            <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-10 h-10 rounded-full object-cover mb-1" />
            <p className="text-white font-semibold text-base">Bienvenue sur KartQuiz</p>
          </div>
          <div className="flex-1 min-h-0 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-4 shadow-xl flex flex-col">
            <GuestShareCard pendingShare={pendingShare} onBack={() => setShareGuestMode(false)} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 pt-24"
      style={bgStyle}
    >
      <div className="relative w-full max-w-sm">
        {inApp ? (
          <InAppBrowserWarning />
        ) : (
          <>
            {/* Carte connexion */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              {guestMode ? (
                <GuestCard onBack={() => setGuestMode(false)} />
              ) : (
                <>
                  {/* En-tête dans la carte */}
                  <div className="flex items-center mb-3">
                    <div className="flex-1" />
                    <img
                      src="/android-chrome-192x192.png"
                      alt="KartQuiz"
                      className="w-16 h-16 drop-shadow-lg rounded-lg"
                    />
                    <div className="flex-1 flex justify-end">
                      <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide shadow">
                        BETA
                      </span>
                    </div>
                  </div>
                  <h1 className="font-brand text-4xl text-white drop-shadow text-center mb-5">KartQuiz</h1>

                  {/* Bandeau quiz partagé en attente */}
                  {pendingShare && (
                    <div className="mb-4 bg-primary/15 border border-primary/30 rounded-xl px-3 py-2.5 text-center">
                      <p className="text-white/70 text-xs">🎯 Quiz partagé avec toi</p>
                      <p className="text-white font-medium text-sm mt-0.5">"{pendingShare.deckTitle}"</p>
                    </div>
                  )}

                  <h2 className="text-base font-semibold text-white mb-4 text-center">Connexion / Création de compte</h2>

                  <SsoButtons loading={loading} onSso={handleSso} />

                  {error && (
                    <p className="mt-3 text-red-300 text-sm bg-red-500/15 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
                  )}

                  {/* Séparateur + accès invité */}
                  <div className="relative mt-4 mb-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/15" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-gray-900 px-3 text-white/40 text-xs">ou</span>
                    </div>
                  </div>

                  {pendingShare ? (
                    <button
                      type="button"
                      onClick={() => setShareGuestMode(true)}
                      className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition"
                    >
                      🚀 Jouer sans compte →
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setGuestMode(true)}
                      className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition"
                    >
                      🏁 Rejoindre en tant qu'invité
                    </button>
                  )}
                </>
              )}
            </div>

            {!guestMode && !shareGuestMode && (
              <div className="text-center mt-5">
                <Link to="/" className="text-white/70 hover:text-white text-sm transition">
                  Découvrir KartQuiz
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
